import { useEffect, useRef } from 'react';
import { EMPTY, catchError, filter, from, map, switchMap, tap } from 'rxjs';
import type { Vector3Like } from 'three';

import { readAsText, useVToast } from '@/package';
import type { FrameData } from '@/type';

import { useDrop } from '../element';

export type ExportedJSON = {
  filename: string;
  description: string;
  key: string;
  tags: Tag[];
  objects: Object[];
  figures: Figure[];
};

export type Tag = {
  key: string;
  name: string;
  value: any;
  labelerLogin: string;
  createdAt: string;
  updatedAt: string;
};

export type Object = {
  key: string;
  label_id: string;
  classTitle: string;
  alias: string;
  tags: ObjectTag[];
  labelerLogin: string;
  createdAt: string;
  updatedAt: string;
};

export type ObjectTag = {
  key: string;
  name: string;
  value: string;
  labelerLogin: string;
  createdAt: string;
  updatedAt: string;
};

export type Label = {
  label_id: string;
  color: string;
  name: string;
  alias: string;
  type: string;
};

export type Figure = {
  key: string;
  objectKey: string;
  geometryType: string;
  geometry: Geometry;
  labelerLogin: string;
  class: string;
  alias: string;
  confidence: number;
  createdAt: string;
  updatedAt: string;
  trackId: string;
};

export type Geometry = {
  position: Vector3Like;
  dimension: Vector3Like;
  rotation: Vector3Like;
};

export const useDnDWorkDataJson = (
  onDrop: (boxList: FrameData['workData']) => void,
) => {
  const { addToast } = useVToast();
  const $draggableRef = useRef<HTMLDivElement>(null);
  const dropController = useDrop(event => {
    const files = event.dataTransfer?.files;

    if (!files) return;

    const [file] = files;

    const sub = from(file.arrayBuffer())
      .pipe(
        map(buf => new Blob([buf], { type: 'application/json' })),
        switchMap(blob => readAsText(blob)),
        map(text => JSON.parse(text) as ExportedJSON),
        map(json => ({
          labels: json.tags.find(tag => tag.name === 'labels')
            ?.value as Label[],
          objects: json.objects,
          figures: json.figures,
        })),
        filter(
          data =>
            data.labels.length > 0 &&
            data.objects.length > 0 &&
            data.figures.length > 0,
        ),
        tap(data => {
          const labelMap = data.labels.reduce((acc, label) => {
            acc.set(label.label_id, label);

            return acc;
          }, new Map<string, Label>());

          const objectMap = data.objects.reduce((acc, object) => {
            acc.set(object.key, object);

            return acc;
          }, new Map<string, Object>());

          const boxList = data.figures.map(figure => {
            const object = objectMap.get(figure.objectKey);

            if (!object) return null;

            const label = labelMap.get(object.label_id);

            if (!label) return null;

            return {
              dimension: [
                figure.geometry.dimension.x,
                figure.geometry.dimension.y,
                figure.geometry.dimension.z,
              ],
              position: [
                figure.geometry.position.x,
                figure.geometry.position.y,
                figure.geometry.position.z,
              ],
              rotation: [
                figure.geometry.rotation.x,
                figure.geometry.rotation.y,
                figure.geometry.rotation.z,
              ],
              id: figure.key,
              trackId: figure.trackId.match(/\d{19}/)?.[0],
              confidence: figure.confidence,
              label: label.label_id,
            } as FrameData['workData'][0];
          }) as FrameData['workData'];

          onDrop(boxList);
        }),
        catchError(() => {
          addToast({
            autoClose: true,
            message: 'Not valid JSON file',
            variant: 'error',
          });

          return EMPTY;
        }),
      )
      .subscribe(() => {
        sub.unsubscribe();
      });
  });

  useEffect(() => {
    if ($draggableRef.current) {
      dropController.connect($draggableRef.current);
    }
  }, []);

  return { $draggableRef, ...dropController };
};
